<template>
  <transition name="bg-modal">
    <div
      v-if="visible"
      class="bg-modal-overlay"
      role="presentation"
      @click.self="close"
    >
      <div
        class="bg-login-modal bg-point-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bg-point-modal-title"
        @keydown.esc.prevent="close"
      >
        <header class="bg-login-modal__header">
          <h2 id="bg-point-modal-title" class="bg-point-modal__title">{{ $t('mypage.pointConvertTitle') }}</h2>
          <button
            type="button"
            class="bg-login-modal__close"
            :aria-label="$t('common.close')"
            @click="close"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </header>

        <div class="bg-point-modal__body">
          <p v-if="loading" class="bg-point-modal__status">{{ $t('mypage.pointConvertLoading') }}</p>
          <template v-else>
            <ul class="bg-point-modal__summary">
              <li>
                <span>{{ $t('mypage.balance') }}</span>
                <strong>{{ formatBalance(balance) }}</strong>
              </li>
              <li>
                <span>{{ $t('mypage.point') }}</span>
                <strong>{{ formatPoint(point) }}</strong>
              </li>
            </ul>

            <form class="wallet-form" @submit.prevent="submit">
              <div class="wallet-form__field wallet-form__field--amount">
                <span class="wallet-form__label">{{ $t('mypage.pointConvertAmount') }} <em>({{ $t('wallet.required') }})</em></span>
                <input
                  :value="amountDisplay"
                  type="text"
                  inputmode="decimal"
                  class="wallet-form__amount-input"
                  :placeholder="$t('mypage.pointConvertAmountPlaceholder')"
                  autocomplete="off"
                  :disabled="submitting"
                  @input="onAmountInput"
                  @focus="onAmountFocus"
                  @blur="onAmountBlur"
                >
              </div>

              <div class="wallet-form__quick">
                <button type="button" :disabled="submitting" @click="addAmount(10)">{{ siteCurrency }}10</button>
                <button type="button" :disabled="submitting" @click="addAmount(50)">{{ siteCurrency }}50</button>
                <button type="button" :disabled="submitting" @click="addAmount(100)">{{ siteCurrency }}100</button>
                <button type="button" :disabled="submitting" @click="setAllPoint">{{ $t('mypage.pointConvertAll') }}</button>
                <button type="button" class="wallet-form__quick-reset" :disabled="submitting" @click="resetAmount">{{ $t('common.refresh') }}</button>
              </div>

              <p v-if="errorMessage" class="bg-point-modal__error" role="alert">{{ errorMessage }}</p>

              <div class="wallet-form__actions wallet-form__actions--solo">
                <button
                  type="submit"
                  class="wallet-form__submit"
                  :disabled="submitting || loading || !canSubmit"
                >
                  {{ submitting ? $t('mypage.pointConvertSubmitting') : $t('mypage.pointConvertSubmit') }}
                </button>
              </div>
            </form>

            <ul class="wallet-form__notes">
              <li>{{ $t('mypage.pointConvertNote1') }}</li>
              <li>{{ $t('mypage.pointConvertNote2') }}</li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { GameService, WalletService } from '@/services'
import {
  formatDecimalInput,
  formatPointAmount,
  parsePlainDecimalValue,
  roundToDecimals,
  sanitizePlainDecimalInput
} from '@/utils/format'

export default {
  name: 'BlackGoldPointConvertModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      submitting: false,
      balance: 0,
      point: 0,
      amount: 0,
      amountDisplay: '',
      amountInputFocused: false,
      errorMessage: ''
    }
  },
  computed: {
    maxPoint () {
      return roundToDecimals(this.point, 2)
    },
    canSubmit () {
      const amount = roundToDecimals(this.amount, 2)
      if (amount <= 0) return false
      return amount <= this.maxPoint
    }
  },
  watch: {
    visible (open) {
      if (open) {
        this.resetForm()
        this.loadBalances()
      }
    }
  },
  methods: {
    formatPoint (value) {
      return `${formatPointAmount(value)} P`
    },
    close () {
      if (this.submitting) return
      this.$emit('close')
    },
    resetForm () {
      this.amount = 0
      this.amountDisplay = ''
      this.amountInputFocused = false
      this.errorMessage = ''
      this.submitting = false
    },
    async loadBalances () {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await GameService.getBalanceApi()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.balance = Number(body.data.balance) || 0
          this.point = Number(body.data.point) || 0
        } else {
          this.errorMessage = (body && body.message) ? body.message : this.$t('mypage.pointConvertLoadError')
        }
      } catch (err) {
        this.errorMessage = this.$t('mypage.pointConvertLoadError')
      } finally {
        this.loading = false
      }
    },
    validateAmount () {
      const amount = roundToDecimals(this.amount, 2)
      if (amount <= 0) {
        return this.$t('mypage.pointConvertAmountRequired')
      }
      if (amount > this.maxPoint) {
        return this.$t('mypage.pointConvertAmountExceed')
      }
      return ''
    },
    syncAmountError () {
      const amount = roundToDecimals(this.amount, 2)
      if (amount === 0) {
        this.errorMessage = ''
        return
      }
      this.errorMessage = this.validateAmount()
    },
    setAmountDisplay (value) {
      const rounded = roundToDecimals(value, 2)
      this.amount = rounded
      if (rounded <= 0) {
        this.amountDisplay = ''
        return
      }
      this.amountDisplay = this.amountInputFocused
        ? String(rounded)
        : formatDecimalInput(rounded)
    },
    onAmountFocus () {
      this.amountInputFocused = true
      if (this.amountDisplay) {
        this.amountDisplay = sanitizePlainDecimalInput(this.amountDisplay)
      }
    },
    onAmountBlur () {
      this.amountInputFocused = false
      this.amount = parsePlainDecimalValue(this.amountDisplay)
      this.amountDisplay = this.amount > 0 ? formatDecimalInput(this.amount) : ''
      this.syncAmountError()
    },
    onAmountInput (event) {
      const sanitized = sanitizePlainDecimalInput(event.target.value)
      this.amountDisplay = sanitized
      this.amount = parsePlainDecimalValue(sanitized)
      this.syncAmountError()
    },
    addAmount (value) {
      const next = roundToDecimals((Number(this.amount) || 0) + Number(value), 2)
      this.setAmountDisplay(next)
      this.syncAmountError()
    },
    setAllPoint () {
      this.setAmountDisplay(this.maxPoint)
      this.errorMessage = ''
    },
    resetAmount () {
      this.amount = 0
      this.amountDisplay = ''
      this.errorMessage = ''
    },
    async submit () {
      if (this.submitting || this.loading) return

      this.amount = parsePlainDecimalValue(this.amountDisplay)

      if (!this.canSubmit) {
        const validationError = this.validateAmount()
        if (validationError) this.errorMessage = validationError
        return
      }

      const amount = roundToDecimals(this.amount, 2)

      this.submitting = true
      this.errorMessage = ''
      try {
        const res = await WalletService.compRequest({ amount: amount })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          this.resetAmount()
          await this.loadBalances()
          this.$emit('success')
          this.$emit('close')
          return
        }
        this.errorMessage = (body && body.message) ? body.message : this.$t('mypage.pointConvertFail')
      } catch (err) {
        this.errorMessage = this.$t('mypage.pointConvertFail')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
